import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import isNil from 'lodash/isNil';

import { RightArrowAltIcon } from '../../../icon/right_arrow_alt_icon/RightArrowAltIcon';
import { LeftArrowAltIcon } from '../../../icon/left_arrow_alt_icon/LeftArrowAltIcon';
import { GoalIcon } from '../../../../components/icon/goal_icon/GoalIcon';

import { useStepNavContext } from './StepNavContext';
import type { StepNavProps } from './type';

const SecionProgressDashedLine = (props: { variant: 'dashed' | 'filled' }) => {
  const { variant } = props;
  return (
    <svg width="1000" height="30" viewBox="0 -2 1000 34">
      <line
        x1="-1000"
        y1="15"
        x2="1000"
        y2="15"
        stroke={variant === 'dashed' ? '#D9E0E9' : '#FF3975'}
        strokeWidth={variant === 'dashed' ? 1 : 2}
        strokeDasharray={variant === 'dashed' ? 2.5 : undefined}
      />
    </svg>
  );
};
const SectionProgressCricle = (props: { progress?: number; number?: number }) => {
  const { progress, number } = props;
  return (
    <div>
      <div style={{ position: 'relative', height: 30, width: 30 }}>
        <svg width="30" height="30" viewBox="-2 -2 34 34" style={{ transform: 'rotate(-184deg)' }}>
          <circle r="15" cx="15" cy="15" fill="transparent" stroke="#D9E0E9" strokeWidth="1" strokeDasharray="2.5" />
          <circle
            r="15"
            cx="15"
            cy="15"
            stroke="#FF3975"
            strokeWidth="2"
            strokeLinecap="butt"
            strokeDashoffset={94 * (1 - (typeof progress === 'number' ? progress : 0)) + 'px'}
            fill="transparent"
            strokeDasharray="94.2px"
          />
        </svg>
        {typeof number === 'number' && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              color: '#FF3975',
            }}
          >
            {number}
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryProgress = (props: {
  allProgress: number;
  currentCategoryName: string;
  currentCategoryNumber: number;
  currentCategoryProgress: number;
  nextCategoryName?: string;
}) => {
  const titleStyle: React.CSSProperties = {
    position: 'absolute',
    width: 'fit-content',
    transform: 'translateX(-50%)',
    fontWeight: 700,
    letterSpacing: 1,
  };
  const { allProgress, currentCategoryName, currentCategoryNumber, currentCategoryProgress, nextCategoryName } = props;
  return (
    <div style={{ display: 'flex', minWidth: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ display: 'flex', fontSize: 12, height: 20 }}>
          <div style={{ flex: 2 }} />
          <div>
            <div style={{ ...titleStyle, color: '#000' }}>{currentCategoryName}</div>
          </div>
          <div style={{ flex: 3 }} />
          <div>
            <div style={titleStyle}>{nextCategoryName}</div>
            <div style={{ ...titleStyle }}>
              <div style={{ display: 'block', background: '#FFFFFF' }}>
                <div style={{ maskImage: 'linear-gradient(90deg,  rgba(0, 0, 0, 1) 40%, transparent)' }}>
                  {nextCategoryName}
                </div>
              </div>
            </div>
          </div>
          <div style={{ flex: 2 }} />
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 2, overflow: 'hidden', position: 'relative', left: 1 }}>
            <SecionProgressDashedLine variant="filled" />
          </div>
          <SectionProgressCricle progress={currentCategoryProgress} number={currentCategoryNumber} />
          <div style={{ flex: 3, overflow: 'hidden' }}>
            <SecionProgressDashedLine variant="dashed" />
          </div>
          {nextCategoryName ? (
            <SectionProgressCricle />
          ) : (
            <div style={{ width: 30, overflow: 'hidden' }}>
              <SecionProgressDashedLine variant="dashed" />
            </div>
          )}
          <div style={{ flex: 2, overflow: 'hidden' }}>
            <SecionProgressDashedLine variant="dashed" />
          </div>
        </div>
      </div>
      <div
        style={{ alignSelf: 'flex-end', paddingBottom: 4, paddingLeft: 4, display: 'flex', flexDirection: 'column' }}
      >
        <GoalIcon />
        <div style={{ fontSize: 12, fontWeight: 700, marginTop: -2, color: 'black' }}>
          {Math.round(allProgress * 100)}%
        </div>
      </div>
    </div>
  );
};

/**
 * Step navigation bar.
 */
const UnstyledStepNav: FunctionComponent<StepNavProps> = ({
  className,
  goNext,
  goBack,
  stepIdx,
  stepCnt,
  categories,
  stepIndexInCategory,
  stepCategoryCount,
  stepCategoryName,
  appData,
  categoryName,
}) => {
  const { setStepNavData, progress } = useStepNavContext();

  const shouldUseCategoryProgress = useMemo(() => {
    // Keep this commented out code for future reference
    // if (appData.selectedService?.insurance) {
    //   const variant = growthBookInstance?.getFeatureValue(ABTestStepProgress.storageKey, 0);
    //   return variant === 2;
    // }
    return false;
  }, []);

  const stepTitle = useMemo(() => {
    if (isNil(stepIdx) || isNil(stepCnt) || (stepCnt === 1 && stepIdx === 0)) return '';
    /**
     * 1.Show 'STEP X • STEP_CATEGORY_NAME' in garage/dwep flow, other flows remain unchanged.
     * 2.ServiceSplit page only shows step category name.
     */
    if (categoryName) {
      return categoryName;
    }
    if (!appData.isOnboardingLite && stepCategoryCount && !isNil(stepIndexInCategory) && stepCategoryName) {
      return (
        <Box display="flex" alignItems="center">
          <span>
            STEP {stepIndexInCategory + 1}/{stepCategoryCount}
          </span>
          <div style={{ width: 4, height: 4, backgroundColor: '#a0afbd', borderRadius: '100%', margin: '0 1rem' }} />
          <span>{stepCategoryName}</span>
        </Box>
      );
    } else {
      return `STEP ${stepIdx + 1}/${stepCnt}`;
    }
  }, [
    stepIdx,
    stepCnt,
    categoryName,
    appData.isOnboardingLite,
    stepCategoryCount,
    stepIndexInCategory,
    stepCategoryName,
  ]);

  useEffect(() => {
    setStepNavData({ stepTitle });
  }, [setStepNavData, stepTitle]);

  /* Wrapping nav in `motion.div` allows us to avoid showing the same component from different steps in
    between transitions by setting opacity to 0. */
  return (
    <motion.div
      variants={{ exit: { opacity: 0 }, enter: { opacity: 0 }, center: { opacity: 1 } }}
      transition={{ opacity: 0 }}
    >
      <Box flex={1} display="flex" flexDirection="row" alignItems="center" className={className}>
        <Box className="iconContainer" style={shouldUseCategoryProgress ? { paddingTop: 12 } : undefined}>
          {goBack && (
            <IconButton
              aria-label="back"
              onClick={() => {
                goBack();
              }}
            >
              <LeftArrowAltIcon />
            </IconButton>
          )}
        </Box>

        <Box flex={1} textAlign="center" className="titleContainer stepCountTitle">
          {shouldUseCategoryProgress &&
          stepCategoryName &&
          stepIndexInCategory !== undefined &&
          categories &&
          progress ? (
            <CategoryProgress
              allProgress={progress.allProgress}
              currentCategoryNumber={stepIndexInCategory + 1}
              currentCategoryName={stepCategoryName}
              currentCategoryProgress={progress?.categoryProgress}
              nextCategoryName={categories[stepIndexInCategory + 1]}
            />
          ) : (
            stepTitle
          )}
        </Box>

        <Box className={'iconContainer'} style={shouldUseCategoryProgress ? { paddingTop: 12 } : undefined}>
          {goNext && (
            <IconButton
              aria-label="next"
              onClick={() => {
                goNext(appData);
              }}
            >
              <RightArrowAltIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

export const StepNav = styled(UnstyledStepNav)`
  margin-bottom: 12px;

  .iconContainer {
    width: 48px;
    height: 48px;
  }

  .titleContainer {
    min-width: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    line-height: 18px
    letter-spacing: 2px;
    font-weight: 700;
    color: #a0afbd;
  }
`;
